import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Box, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormatDateHook } from "hooks";
import { useDateFormatSelect } from "store";
import { DebtSegment } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { EditChoice } from "../types";
import { OptionCard } from "../OptionCard";
import { QuestionSlot } from "../QuestionSlot";

interface StepEditChoiceProps {
  existingSegments: DebtSegment[];
  existingWithdrawals: DebtSegment[];
  editChoice: EditChoice | null;
  setEditChoice: (choice: EditChoice) => void;
  selectedSegmentIdx: number | null;
  setSelectedSegmentIdx: (idx: number) => void;
  selectedWithdrawalIdx: number | null;
  setSelectedWithdrawalIdx: (idx: number) => void;
  newTermDate: string | null;
  setNewTermDate: (date: string | null) => void;
}

export const Step0EditChoice: React.FC<StepEditChoiceProps> = ({
  existingSegments, existingWithdrawals,
  editChoice, setEditChoice,
  selectedSegmentIdx, setSelectedSegmentIdx,
  selectedWithdrawalIdx, setSelectedWithdrawalIdx,
  newTermDate, setNewTermDate
}) => {
  const { t } = useTranslation("debt");
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();

  const getSegmentLabel = (index: number): string => {
    if (existingSegments.length === 1) return t("segment.initial");
    if (index === existingSegments.length - 1) return t("segment.current");
    const nextDate = existingSegments[index + 1].start_date;
    const start = formatDate({ date: existingSegments[index].start_date });
    const end = formatDate({ date: dayjs(nextDate).subtract(1, "day") });
    if (index === 0) return t("segment.initialRange", { start, end });
    return t("segment.dateRange", { start, end });
  };

  const stepperItems = useMemo(() => {
    const base = existingSegments.map((s) => ({ date: s.start_date, isNew: false }));
    if (!newTermDate) return base;
    const all = [...base, { date: newTermDate, isNew: true }];
    all.sort((a, b) => (a.date < b.date ? -1 : 1));
    return all;
  }, [existingSegments, newTermDate]);

  const newTermStepIdx = newTermDate ? stepperItems.findIndex((item) => item.isNew) : -1;

  // Group withdrawal segments by deal_id; sort chronologically so [0] = first (drawdown date)
  const groupedWithdrawals = useMemo(() => {
    const map = new Map<number, DebtSegment[]>();
    for (const seg of existingWithdrawals) {
      const existing = map.get(seg.deal_id) ?? [];
      map.set(seg.deal_id, [...existing, seg]);
    }
    return Array.from(map.values()).map(segs =>
      [...segs].sort((a, b) => (a.start_date < b.start_date ? -1 : 1))
    );
  }, [existingWithdrawals]);

  const isEditingWithdrawal = existingSegments.some(s => s.is_withdrawal);
  const showDrawdownOption = !isEditingWithdrawal && groupedWithdrawals.length > 0;
  const isCreditFacility = !isEditingWithdrawal && existingSegments.some(s => s.is_facility && !s.is_withdrawal);

  return (
    <Box>
      <QuestionSlot visible label={t("wizard.editChoice.question")}>
        <Stack spacing={2}>
          <OptionCard
            value="change"
            selected={editChoice === "change"}
            onSelect={() => setEditChoice("change")}
            title={t("wizard.editChoice.changeTitle")}
            subtitle={t("wizard.editChoice.changeDesc")}
          />
          <OptionCard
            value="new_term"
            selected={editChoice === "new_term"}
            onSelect={() => setEditChoice("new_term")}
            title={t("wizard.editChoice.newTermTitle")}
            subtitle={t("wizard.editChoice.newTermDesc")}
          />
          {showDrawdownOption && (
            <OptionCard
              value="edit_drawdown"
              selected={editChoice === "edit_drawdown"}
              onSelect={() => setEditChoice("edit_drawdown")}
              title={t("wizard.editChoice.editDrawdownTitle")}
              subtitle={t("wizard.editChoice.editDrawdownDesc")}
            />
          )}
          {isCreditFacility && (
            <OptionCard
              value="add_drawdown"
              selected={editChoice === "add_drawdown"}
              onSelect={() => setEditChoice("add_drawdown")}
              title={t("wizard.editChoice.addDrawdownTitle")}
              subtitle={t("wizard.editChoice.addDrawdownDesc")}
            />
          )}
        </Stack>
      </QuestionSlot>

      {editChoice === "change" && existingSegments.length > 1 && (
        <QuestionSlot visible label={t("wizard.editChoice.whichTerm")}>
          <Stack spacing={2}>
            {existingSegments.map((_, i) => (
              <OptionCard
                key={i}
                value={String(i)}
                selected={selectedSegmentIdx === i}
                onSelect={() => setSelectedSegmentIdx(i)}
                title={getSegmentLabel(i)}
              />
            ))}
          </Stack>
        </QuestionSlot>
      )}

      {editChoice === "new_term" && (
        <QuestionSlot visible label={t("wizard.editChoice.effectiveDateQuestion")}>
          <DesktopDatePicker
            format={dateFormat || "YYYY-MM-DD"}
            views={["year", "month", "day"]}
            label={t("wizard.editChoice.effectiveDateLabel")}
            value={newTermDate ? dayjs(newTermDate) : null}
            onChange={(v) => setNewTermDate(v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
          />
          <Box sx={{ mt: 4 }}>
            <Stepper activeStep={newTermStepIdx} alternativeLabel>
              {stepperItems.map((item, i) => (
                <Step key={i}>
                  <StepLabel
                    optional={item.isNew ? (
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        {t("wizard.editChoice.new")}
                      </Typography>
                    ) : undefined}
                  >
                    {formatDate({ date: item.date })}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </QuestionSlot>
      )}

      {editChoice === "edit_drawdown" && groupedWithdrawals.length > 1 && (
        <QuestionSlot visible label={t("wizard.editChoice.whichDrawdown")}>
          <Stack spacing={2}>
            {groupedWithdrawals.map((group, i) => (
              <OptionCard
                key={i}
                value={String(i)}
                selected={selectedWithdrawalIdx === i}
                onSelect={() => setSelectedWithdrawalIdx(i)}
                title={group[group.length - 1].deal_title || `Drawdown ${i + 1}`}
                subtitle={formatDate({ date: group[0].start_date })}
              />
            ))}
          </Stack>
        </QuestionSlot>
      )}
    </Box>
  );
};
